import { FormEventHandler, ReactNode } from "react";
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form";

import { Separator } from "../../../components/ui/common/separator";
import { TwScope } from "../../../components/ui/TwScope";

interface AuthPageShellProps<TFieldValues extends FieldValues> {
  eyebrow: string;
  title: string;
  subtitle: string;
  /** The full `useForm()` return, so `FormInput`/`PasswordField` (built on shadcn's `Form` primitives) can read field state via `useFormContext`. */
  methods: UseFormReturn<TFieldValues>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  children: ReactNode;
  /** Links below the form, e.g. "Back to sign in". */
  footer?: ReactNode;
}

/**
 * Shared page shell for the redesigned auth screens (Login, Forgot password,
 * Reset password): the "Nature Preserve / {eyebrow}" label, heading,
 * subtitle, a divider, then the form itself.
 */
export function AuthPageShell<TFieldValues extends FieldValues>({
  eyebrow,
  title,
  subtitle,
  methods,
  onSubmit,
  children,
  footer
}: AuthPageShellProps<TFieldValues>) {
  return (
    <TwScope className="tw-flex tw-min-h-full tw-justify-center tw-px-4 tw-py-10 sm:tw-px-6 sm:tw-py-16">
      <div className="tw-w-full tw-max-w-xl">
        <p className="tw-font-mono tw-text-xs tw-uppercase tw-tracking-[0.14em] tw-text-muted-foreground">
          Nature Preserve / {eyebrow}
        </p>
        <h1 className="tw-mt-3 tw-text-4xl tw-font-medium tw-text-foreground sm:tw-text-5xl">{title}</h1>
        <p className="tw-mt-3 tw-text-sm tw-text-muted-foreground">{subtitle}</p>
        <Separator className="tw-my-8" />
        <FormProvider {...methods}>
          <form noValidate onSubmit={onSubmit} className="tw-space-y-6">
            {children}
          </form>
        </FormProvider>
        {footer && <div className="tw-mt-6 tw-space-y-2">{footer}</div>}
      </div>
    </TwScope>
  );
}
